import { cloneDeep, merge } from 'lodash';
import { comparisonTypes, rootNode, unavailableNode, nodeLayoutConfig } from './constants';
import { validateLinkCriterias } from './validation';
import { getNodeStepName } from '../helpers/node';



/**
 * Given a Call Flow Tree object coming from the backend and the list of available nodes 
 * it generates the scene to display the call flow,
 * @param {*} callFlowTree Call Flow Tree object coming from the backend request
 * @param {*} availableNodes list of available nodes to select with their data & styling
 * @returns The full call flow scene, which contains the call flow name, the nodes, the links and the container
 */
export function prepareScene(callFlowTree, availableNodes) {
  const name = callFlowTree.name;
  const nodes = extractNodes(callFlowTree.nodes);
  nodes.unshift(
    {
      ...rootNode,
      x: callFlowTree.root.posX,
      y: callFlowTree.root.posY,
      hasChild: Boolean(callFlowTree.root?.childLinks?.length)
    }
  );
  const links = extractLinks(callFlowTree.root, callFlowTree.nodes, availableNodes);

  const container = {
    centerX: callFlowTree.centerX,
    centerY: callFlowTree.centerY,
    scale: callFlowTree.zoom
  };

  const scene = { name, nodes, links, container };

  return scene;
}

/**
 * Receives a list of nodes from the backend and parses them into a call flow scene node
 * @param {*} nodes list of nodes as they come from the backend tree
 * @returns list of nodes to use in a call flow scene
 */
export function extractNodes(nodes) {
  let sceneNodes = Object.entries(nodes);
  sceneNodes = sceneNodes.map((n) => {
    return {
      x: n[1].node.posX,
      y: n[1].node.posY,
      ...n[1].node,
      hasChild: Boolean(n[1].childLinks?.length)
    };
  });
  return sceneNodes;
}

/**
 * Generates the links for the call flow scene
 * @param {*} root Root node from the backend call flow tree
 * @param {*} nodes List of nodes from the backend call flow tree
 * @param {*} availableNodes list of available nodes to select with their data & styling
 * @returns A list of the call flow scene links, the link data contains the origin & target nodes of the link, the slots it belongs to and the link data related to filters
 */
export function extractLinks(root, nodes, availableNodes) {

  // Node links as key/value  array
  let nodeLinks = Object.entries(nodes);

  // Create a map of the nodes to be able to refer back to the childNodeId of a link
  const nodesMap = new Map(nodeLinks.map(key => [key[0], key[1].node]));

  /**
   * ROOT LINKS - START
   * Create the first link starting from the root (Inbound Call)
   */
  const rootLink = root.childLinks.map(({ childNodeId, ...x }) => {

    // Get the target nodeTypeId using the node map created earlier
    const thisTargetNode = nodesMap.get(childNodeId);

    // Find the target node option data using the targetNodeTypeId
    const thisTargetNodeDefaults = availableNodes.find((target) => {
      return target.nodeTypeId === thisTargetNode.nodeTypeId;
    }) || unavailableNode;

    return {
      originSlot: 0,
      originNode: {
        id: root.id,
        x: root.posX,
        y: root.posY,
        isValid: root.isValid,
        nodeTypeId: root.nodeTypeId
      },
      targetSlot: 0,
      targetNode: {
        id: childNodeId,
        displayName: thisTargetNodeDefaults.displayName,
        x: 0,
        y: 0,
        nodeOptions: thisTargetNodeDefaults.nodeOptions ? thisTargetNodeDefaults.nodeOptions : {}
      },
      ...x
    };
  });
  /**
   * ROOT LINKS - END
   */

  // Dig through the links and generate a link item for all child links
  nodeLinks = nodeLinks.map((x) => {

    // Find the origin node option data, ie. accent color and size
    const thisOriginNode = availableNodes.find((origin) => {
      return origin.nodeTypeId === x[1].node.nodeTypeId;
    }) || unavailableNode;

    // Start iterating over the child links
    return x[1].childLinks?.map(({ childNodeId, ...y }) => {
      
      // Check for link criteria validation errors
      const linkValidationErrors = validateLinkCriterias(y);

      // Get the target nodeTypeId using the node map created earlier
      const thisTargetNode = nodesMap.get(childNodeId);

      // Find the target node option data using the targetNodeTypeId
      const thisTargetNodeDefaults = availableNodes.find((target) => {
        return target.nodeTypeId === thisTargetNode.nodeTypeId;
      }) || unavailableNode;

      // Determine origin node output slot
      let thisOriginSlot;
      if (thisOriginNode.nodeTypeId == 'MenuNode' || thisOriginNode.nodeTypeId == 'SplitTestNode' || thisOriginNode.nodeTypeId == 'RouterNode' || thisOriginNode.nodeTypeId == 'HoursOfOperationNode') {
        if (y.onSuccess) {
          thisOriginSlot = 0;
        } else {
          thisOriginSlot = 1;
        }
      } else {
        if (thisOriginNode.nodeTypeId == 'GoToNode' || thisOriginNode.nodeTypeId == 'UnavailableNode') {
          thisOriginSlot = 0;
        } else {
          thisOriginSlot = thisOriginNode.potentialLinks.findIndex((potentialLink) => compareLinkCriteria(potentialLink.minimumCriteria, y.criteria[0]) && potentialLink.onSuccess == y.onSuccess);
        }
      }

      if (thisOriginNode.nodeTypeId === 'RouterNode') {
        y.criteria.forEach((c) => {
          if (c.bulkCriteria) {
            c.tagCriteria = c.tagCriteria ? cloneDeep(c.tagCriteria) : [];
            c.tagCriteria.push({
              tagId: c.bulkCriteria.tagId,
              value: c.bulkCriteria.id,
              forBulkTags: true,
              isNegativeMatch: c.bulkCriteria.isNegativeMatch,
              comparisonTypeTemp: comparisonTypes.find(cp => cp.forBulkTags && cp.isNegativeMatch === c.bulkCriteria.isNegativeMatch),
              comparisonType: comparisonTypes.find(cp => cp.forBulkTags && cp.isNegativeMatch === c.bulkCriteria.isNegativeMatch).key
            });
          }
        });
      }


      // Determine the menu press filter path
      let thisFilterValue = null;
      if (thisOriginNode.nodeTypeId === 'MenuNode' && y.onSuccess) {
        const menuTag = 'menu:' + x[1].node.id;
        y.criteria.forEach((c) => {
          c.tagCriteria.forEach((tc) => {
            if (tc.value) thisFilterValue = tc.value;
            // When loading a legacy IVR Wiht Menu tags it might be using menu:menuTagName instead of menu:nodeId
            // If we leave it as it is and they change the menu tag name the route will stop working, so we always turn the tag to use the id instad
            if (tc.tagIds && tc.tagIds[0] !== menuTag) tc.tagIds[0] = menuTag;
            // Related to above, supports the deprecated yet still valid property tagId
            if (tc.tagId != null && tc.tagId !== menuTag) tc.tagId = menuTag;
          });
        });
      }

      if (thisOriginNode.nodeTypeId === 'SplitTestNode') {
        const menuTag = 'splitTest:' + x[1].node.id;
        y.criteria.forEach((c) => {
          c.tagCriteria.forEach((tc) => {
            if (tc.value) thisFilterValue = tc.value;
            // When loading a legacy IVR Wiht Menu tags it might be using menu:menuTagName instead of menu:nodeId
            // If we leave it as it is and they change the menu tag name the route will stop working, so we always turn the tag to use the id instad
            if (tc.tagIds && tc.tagIds[0] !== menuTag) tc.tagIds[0] = menuTag;
            // Related to above, supports the deprecated yet still valid property tagId
            if (tc.tagId != null && tc.tagId !== menuTag) tc.tagId = menuTag;
          });
        });
      }

      // Return data used to generate the link paths
      return {
        originSlot: thisOriginSlot,
        originNode: {
          id: x[1].node.id,
          x: x[1].node.posX,
          y: x[1].node.posY,
          isValid: x[1].node.isValid,
          nodeTypeId: x[1].node.nodeTypeId,
          nodeOptions: thisOriginNode.nodeOptions
        },
        targetSlot: 0,
        targetNode: {
          id: childNodeId,
          displayName: thisTargetNodeDefaults.displayName,
          stepName: getNodeStepName(thisTargetNode),
          x: 0,
          y: 0,
          nodeOptions: thisTargetNodeDefaults.nodeOptions
        },
        filter: thisFilterValue,
        isValid: linkValidationErrors.isSuccessful,
        ...y,
        isOnFailure: !y.onSuccess
      };
    });
  });
  const links = [].concat(rootLink, nodeLinks.flat(1)).filter((link) => !(link.originNode.nodeTypeId == 'GoToNode' && link.onSuccess == true));
  return links.map((link, index) => {
    return {
      id: index,
      ...link
    };
  });
}

/**
 * Given 2 link criterias it compares that their values are not same
 * @param {*} criteria1 Criteria to compare
 * @param {*} criteria2 Criteria to compare
 * @returns True if both criterias are equivalent (same value & negativeMatch), false in any other case
 */
export function compareLinkCriteria(criteria1, criteria2) {
  return criteria1?.tagCriteria?.[0]?.value == criteria2?.tagCriteria?.[0]?.value && criteria1?.tagCriteria?.[0]?.isNegativeMatch == criteria2?.tagCriteria?.[0]?.isNegativeMatch;
}

/**
 * Creates the Link criteria based on the parameters received
 * If the node is a menu node it will build a valid criteria for it using the filter
 * In any other case it will return an array of the criteria
 * @param {*} request.node Origin node of the link criteria
 * @param {*} request.filter Filter value of the criteria
 * @param {*} request.criteria criteria to apply
 * @returns valid criteria for the link
 */
export function buildCriteria({ node, filter, criteria }) {
  if (node.nodeTypeId == 'MenuNode') {
    // Use passed criteria if available (voice response mode), otherwise use filter (DTMF mode)
    if (criteria && criteria.length > 0) {
      return criteria;
    }
    return [{
      tagCriteria: [
        {
          tagIds: ['menu:' + node.id],
          comparisonType: 'EQUALS',
          isNegativeMatch: false,
          value: filter
        }
      ]
    }];
  }
  if (node.nodeTypeId == 'SplitTestNode') {
    return [{
      tagCriteria: [
        {
          tagIds: ['splitTest:' + node.id],
          comparisonType: 'EQUALS',
          isNegativeMatch: false,
          value: filter
        }
      ]
    }];
  }
  if (criteria) {
    return [criteria];
  }
  return [];
}

/**
 * Given a Node and a scene it gives the next valid number to use as the criteria or null if all the numbers (0-9) were used.
 * @param {*} node Node to get the next number filter for
 * @param {*} scene Call flow scene
 * @returns the next valid number to use as the criteria or null if all the numbers (0-9) were used.
 */
export function getPossibleLinkFilter(node, scene) {
  const menuLinks = scene.links.filter(link => {
    return link.originNode.id == node.id && link.onSuccess == true;
  });
  if (menuLinks.length < 10) {
    
    if (node.nodeTypeId == 'SplitTestNode') {
      return menuLinks.length + 1;
    }
    // 0 to 9 array
    let availableNumbers = Array.from({ length: 10 }, (_, i) => (((i + 1) % 10) + 10) % 10);
    availableNumbers = availableNumbers.filter(number => !menuLinks.find(menuLink => {
      return menuLink.filter == number || (menuLink.criteria && menuLink.criteria[0] && (menuLink.criteria[0].value == number || menuLink.criteria[0].tagCriteria[0].value == number));
    }));
    if (availableNumbers.length > 0) {
      return availableNumbers[0];
    }
  }
  return null;
}

/**
 * Gets the next available routeIndex for Voice Response mode on a MenuNode.
 * Unlike DTMF mode which uses digits 0-9, voice response mode uses a sequential routeIndex
 * that serves as a stable identifier for route matching.
 * @param {*} node MenuNode to get the next routeIndex for
 * @param {*} scene Call flow scene
 * @returns the next available routeIndex (1, 2, 3, etc.)
 */
export function getPossibleRouteIndex(node, scene) {
  const menuLinks = scene.links.filter(link => {
    return link.originNode.id == node.id && link.onSuccess == true;
  });

  // Find max routeIndex from existing links
  const maxRouteIndex = menuLinks.reduce((max, link) => {
    return Math.max(max, link.routeIndex || 0);
  }, 0);

  return maxRouteIndex + 1;
}

/**
 * Updates all the nodes with the data from the links and availablenodes
 * @param {*} nodes List of the Scene nodes to refresh
 * @param {*} links List of the call flow scene links
 * @param {*} availableNodes List of the available backend nodes
 * @returns List of scene nodes 
 */
export function prepareNodesAndLinking(nodes, links, availableNodes) {
  return nodes
    .map((node) => {
      const thisNode = availableNodes.find((n) => {
        return n.nodeTypeId === node.nodeTypeId;
      }) || unavailableNode;

      if (!thisNode) {
        return null;
      }

      let newNode = createNode(thisNode, node.id);

      newNode = merge(newNode, node);

      // Update Menu node output labels based on voice response mode
      if (node.nodeTypeId === 'MenuNode' && newNode.settings?.voiceResponse) {
        const onKeyPressOutput = newNode.outputs.find(output => output.name === 'On Key Press');
        if (onKeyPressOutput) {
          onKeyPressOutput.name = 'Keyword Routes';
        }
      }

      const inputs = links.filter((link) => {
        return link.targetNode.id === node.id;
      });

      const outputs = links.filter((link) => {
        return link.originNode.id === node.id;
      });

      newNode.inputs.forEach((s, index) => {
        newNode.inputs[index].active = inputs.some((i) => i.targetSlot === index);
      });

      newNode.outputs.forEach((s, index) => {
        newNode.outputs[index].active = outputs.some((i) => i.originSlot === index);
      });

      return newNode;
    });
}

/**
 * Given a available node backend data and it's id it generates the model for the Scene call flow
 * @param {*} node Available node Backend data
 * @param {*} id Id of the node
 * @returns The barebones of a scene node
 */
export function createNode(node, id) {
  const inputs = [];
  const outputs = [];

  if (id !== 'root') {
    inputs.push({
      name: '',
      type: 'event',
      attr: 'input',
    });
  }

  if (node.potentialLinks && !(node.nodeTypeId == 'MenuNode' || node.nodeTypeId == 'SplitTestNode' || node.nodeTypeId == 'RouterNode')) {

    node.potentialLinks.forEach((link) => {
      outputs.push({
        name: link.name.replace(/([a-z])([A-Z])/g, '$1 $2'),
        linkName: link.name,
        onSuccess: link.onSuccess,
        type: 'event',
        attr: 'output',
        minimumCriteria: link.minimumCriteria
      });
    });
  }

  if (node.nodeTypeId == 'MenuNode') {
    outputs.push({
      name: 'On Key Press',
      type: 'event',
      attr: 'output',
      onSuccess: true,
    });
    outputs.push({
      name: 'On Failure',
      type: 'event',
      attr: 'output',
      onSuccess: false,
    });
  }
  if (node.nodeTypeId == 'RouterNode' || node.nodeTypeId == 'SplitTestNode') {
    outputs.push({
      name: 'Routes',
      type: 'event',
      attr: 'output',
      onSuccess: true,
    });
  }

  return {
    id: id,
    x: 0,
    y: 0,
    selected: false,
    nodeType: node.nodeType,
    displayName: node.displayName,
    nodeDescription: node.description,
    nodeOptions: node.nodeOptions,
    potentialLinks: node.potentialLinks,
    parameters: node.parameters,
    inputs: inputs,
    outputs: outputs,
    hasChild: null
  };
}

/**
 * Merges the given options into the given nodes data
 * @param {*} nodes list of scene nodes
 * @param {*} options options to add to the nodes
 * @returns list of the nodes with the merged data
 */
export function mergeNodeData(nodes, options) {
  return nodes.map(node => ({
    ...options.find((option) => (option.nodeTypeId === node.nodeTypeId) && option),
    ...node
  }));
}

/**
 * Returns the connection coordinates for a link to use 
 * @param {*} node Node the link is connected to
 * @param {*} slotNumber slot number of the connection the links belongs to
 * @param {*} isInput marks if the link is deciding an input connection or an output
 * @param {*} container scene container data with position and scale
 * @returns an object with coordinates x & y of the link
 */
export function getConnectionPos(node, slotNumber, isInput, container) {
  if (!node || slotNumber === -1) {
    return undefined;
  }
  let topOffset;
  let slotDist = 0;
  if (!isInput) {
    if (node.nodeOptions.size == 'medium') {
      topOffset = 75;
    } else if (node.nodeOptions.size == 'small') {
      topOffset = 25;
    } else {
      topOffset = (node.outputs.length == 3) ? 68 : (node.outputs.length == 2) ? 75 : 100;
      slotDist = (node.outputs.length == 3) ? 32 : (node.outputs.length == 2) ? 50 : 0;
    }
  } else {
    topOffset = node.nodeOptions.size == 'medium' ? 52 : node.nodeOptions.size == 'small' ? 26 : 75;
  }
  let x = 0;
  let y = 0;
  x += node.x * nodeLayoutConfig.xSpacingMultiplier;
  x += (!isInput && node.outputs.length > slotNumber) ? 190 : 40;
  y += node.y;
  y += topOffset;
  y += slotDist * slotNumber;
  x *= container.scale;
  y *= container.scale;
  x += container.centerX - 10;
  y += container.centerY;
  return { x: x, y: y };
}